import { useState, useEffect } from "react";
import { Loader2, BellRing, Send, PlugZap } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import api from "../../lib/api";

const OPTIONS = [
  { key: "form_submit_email", label: "Website form submissions", desc: "Email me when someone submits a quote/contact/calculator form." },
  { key: "outreach_click_email", label: "Outreach CTA clicks", desc: "Email me each time an outreach recipient clicks a campaign link. Mute this for large blast campaigns." },
  { key: "hot_lead_email", label: "Warming-lead alerts (email)", desc: "Email at each touchpoint milestone (3/5/10…)." },
  { key: "warm_silent_email", label: "Warm-but-silent (email)", desc: "Email when a contact opens 3+ emails but never submits." },
  { key: "slack_enabled", label: "Slack notifications", desc: "Mirror all of the above alerts to your Slack channel (requires a Slack webhook to be configured)." },
];

export default function NotificationSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(false);
  const [integrations, setIntegrations] = useState(null);

  const testSlack = async () => {
    setTesting(true);
    try {
      await api.post("/admin/test-slack");
      toast.success("Test message sent — check your Slack channel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send test message");
    } finally { setTesting(false); }
  };

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/admin/integrations/status").then(({ data }) => setIntegrations(data)).catch(() => {});
  }, []);

  const toggle = async (key) => {
    const next = !settings[key];
    setSettings((s) => ({ ...s, [key]: next }));
    setSaving(key);
    try {
      const { data } = await api.put("/admin/settings", { [key]: next });
      setSettings(data);
      toast.success("Notification preference saved");
    } catch (e) {
      setSettings((s) => ({ ...s, [key]: !next }));
      toast.error("Failed to save");
    } finally { setSaving(null); }
  };

  if (!settings) {
    return <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-flame" /></div>;
  }

  return (
    <div className="mb-6">
      <div className="bg-ink border border-white/10 mb-6" data-testid="integrations-status">
        <div className="flex items-center gap-2 p-4 border-b border-white/10">
          <PlugZap className="h-5 w-5 text-flame" />
          <h3 className="font-display font-bold text-sm uppercase tracking-widest">Integrations</h3>
        </div>
        <div className="divide-y divide-white/5">
          {[
            { key: "hubspot", label: "HubSpot CRM", desc: "One-way sync of leads, touchpoint scores, and engagement timeline notes." },
            { key: "slack", label: "Slack", desc: "Real-time alerts for new leads, warming milestones, and outreach clicks." },
          ].map((it) => {
            const st = integrations?.[it.key];
            const connected = it.key === "hubspot" ? st?.connected : st?.configured;
            const label = st == null ? "Checking…" : connected ? "Connected" : (st?.configured ? "Error" : "Not configured");
            const dot = st == null ? "bg-white/30" : connected ? "bg-emerald-500" : (st?.configured ? "bg-red-500" : "bg-white/30");
            return (
              <div key={it.key} className="p-4 flex items-center justify-between gap-4" data-testid={`integration-${it.key}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm">{it.label}</div>
                  <div className="text-xs text-white/50 mt-0.5">{it.desc}</div>
                  {it.key === "hubspot" && st?.configured && !st?.connected && st?.detail && (
                    <div className="text-xs text-red-400/80 mt-1">{st.detail}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0" data-testid={`integration-${it.key}-status`}>
                  <span className={`h-2 w-2 rounded-full ${dot}`} />
                  <span className="text-xs font-semibold text-white/70">{label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-ink border border-white/10" data-testid="notification-settings">
      <div className="flex items-center gap-2 p-4 border-b border-white/10">
        <BellRing className="h-5 w-5 text-flame" />
        <h3 className="font-display font-bold text-sm uppercase tracking-widest">Notification settings</h3>
      </div>
      <div className="divide-y divide-white/5">
        {OPTIONS.map((o) => (
          <div key={o.key} className="p-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="font-medium text-sm">{o.label}</div>
              <div className="text-xs text-white/50 mt-0.5">{o.desc}</div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {o.key === "slack_enabled" && (
                <button data-testid="test-slack-btn" onClick={testSlack} disabled={testing}
                  className="inline-flex items-center gap-1.5 border border-white/15 text-white/70 hover:text-flame hover:border-flame/50 px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50">
                  {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send test
                </button>
              )}
              {saving === o.key && <Loader2 className="h-3.5 w-3.5 animate-spin text-white/40" />}
              <Switch data-testid={`toggle-${o.key}`} checked={!!settings[o.key]} onCheckedChange={() => toggle(o.key)} />
            </div>
          </div>
        ))}
      </div>
    </div>
    </div>
  );
}
